import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, Button, message, Spin, Alert, Space, Typography, Tooltip, Divider, Tag } from 'antd';
import { SaveOutlined, InfoCircleOutlined, ExperimentOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text, Paragraph } = Typography;

/**
 * Calculator Settings Component
 * Allows admin users to configure calculator settings including biological age calculation method
 */
const CalculatorSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    fetchCalculatorSettings();
  }, []);

  const fetchCalculatorSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/configs/calculator-settings');
      const settings = response.data;
      
      // Convert biologicalAgeMethod to boolean for Switch component
      const formValues = {
        biologicalAgeMethod: settings.biologicalAgeMethod === 'adjusted'
      };
      
      setInitialValues(formValues);
      form.setFieldsValue(formValues);
    } catch (error) {
      console.error('[CalculatorSettings] Error fetching settings:', error);
      message.error('Failed to load calculator settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);

      // Convert boolean back to string enum
      const settingsData = {
        biologicalAgeMethod: values.biologicalAgeMethod ? 'adjusted' : 'phenoage'
      };

      await api.put('/configs/calculator-settings', settingsData);

      message.success('Calculator settings saved successfully');
      setInitialValues(values);

      // Reload settings to confirm they were saved
      await fetchCalculatorSettings();

    } catch (error) {
      console.error('[CalculatorSettings] Error saving settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save calculator settings';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading calculator settings..." />
      </div>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <ExperimentOutlined />
          <span>Calculator Settings</span>
        </Space>
      }
      extra={
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={() => form.submit()}
          loading={saving}
        >
          Save Settings
        </Button>
      }
    >
      <Alert
        message="Calculator Configuration"
        description="Configure calculation methods used throughout the Care360 application. These settings affect how health metrics are calculated and displayed to users."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={initialValues}
      >
        <Divider orientation="left">Biological Age Calculation</Divider>
        
        <Card
          type="inner"
          style={{ marginBottom: 24, backgroundColor: '#fafafa' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Space direction="vertical" size={4}>
                <Space>
                  <Text strong style={{ fontSize: '16px' }}>Biological Age Calculation Method</Text>
                  <Tooltip
                    title={
                      <div>
                        <p><strong>PhenoAge Only (Validated):</strong></p>
                        <p>• Uses only the 9-biomarker Levine PhenoAge algorithm</p>
                        <p>• Peer-reviewed and validated against mortality outcomes (Levine et al. 2018)</p>
                        <p>• Published in <em>Aging</em> journal with multiple independent cohort validations</p>
                        <br />
                        <p><strong>Adjusted Biological Age (Proprietary):</strong></p>
                        <p>• Starts with PhenoAge baseline</p>
                        <p>• Adds adjustments based on overall health metrics, system health scores, and synergistic effects</p>
                        <p>• Proprietary Care360 model that has not been externally validated</p>
                        <p>• Provides more holistic assessment but lacks peer-reviewed validation</p>
                      </div>
                    }
                    overlayStyle={{ maxWidth: '500px' }}
                  >
                    <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
                  </Tooltip>
                </Space>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Choose between validated PhenoAge or proprietary adjusted biological age
                </Text>
              </Space>
            </div>
            <div style={{ marginLeft: 24 }}>
              <Form.Item
                name="biologicalAgeMethod"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checkedChildren={
                    <Space size={4}>
                      <Text style={{ color: 'white', fontSize: '12px' }}>Adjusted</Text>
                      <Tag color="orange" style={{ margin: 0, fontSize: '10px' }}>Proprietary</Tag>
                    </Space>
                  }
                  unCheckedChildren={
                    <Space size={4}>
                      <Text style={{ color: 'white', fontSize: '12px' }}>PhenoAge</Text>
                      <CheckCircleOutlined style={{ color: 'white' }} />
                    </Space>
                  }
                  style={{ minWidth: '140px' }}
                />
              </Form.Item>
            </div>
          </div>
        </Card>

        <Alert
          message="Method Comparison"
          description={
            <div>
              <Paragraph style={{ marginBottom: 12 }}>
                <Text strong>PhenoAge Only (OFF):</Text>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Displays only the baseline PhenoAge value</li>
                  <li>Shows "Biological Age: [value] years (PhenoAge - Validated)"</li>
                  <li>Calculation breakdown shows only the 9 PhenoAge biomarkers</li>
                  <li>No system health adjustments or synergistic effects</li>
                  <li>✅ Research-validated and peer-reviewed</li>
                </ul>
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <Text strong>Adjusted Biological Age (ON - Default):</Text>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Displays the full adjusted biological age (current behavior)</li>
                  <li>Shows "Biological Age: [value] years (Adjusted - Proprietary)"</li>
                  <li>Calculation breakdown shows: PhenoAge + biomarker adjustment + system adjustment + synergistic effects</li>
                  <li>Incorporates additional health metrics beyond the 9-biomarker panel</li>
                  <li>⚠️ Proprietary model without external validation</li>
                </ul>
              </Paragraph>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Alert
          message="Application Scope"
          description="This setting applies universally across all pages: Health Overview, Patient Portal, Biological Age Breakdown modal, and PDF reports. The setting persists across user sessions."
          type="info"
          showIcon
        />
      </Form>
    </Card>
  );
};

export default CalculatorSettings;

